import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ApiService } from './api.service';

export interface CompanySettings {
  id: number;
  company_name: string;
  company_slogan: string;
  date_format: string;
  thanks_message: string;
  address?: string;
  phone?: string;
  email?: string;
  gst_number?: string;
  currency_symbol: string;
  company_logo?: string;
  /** UPI / payment QR image (data URL) shown on invoice PDF beside bank details */
  payment_qr?: string;
  /** A4/A5 invoice & PDF: classic = logo on top; logo_left_center = logo left, company block centered */
  invoice_header_layout?: 'classic' | 'logo_left_center' | string;
  /** Product label layout: classic = name + barcode stack; retail = logo header + QR + price block */
  label_template?: 'classic' | 'retail' | string;
  stock_maintenance_enabled?: number;
  bank_name?: string;
  account_number?: string;
  ifsc_code?: string;
  branch?: string;
  state?: string;
  state_code?: string;
  gst_invoice_prefix?: string;
  non_gst_invoice_prefix?: string;
  /** Default quantity when adding a product to a bill (billing page). Default 1. */
  default_bill_quantity?: number;
  updated_at?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  private settingsSubject = new BehaviorSubject<CompanySettings | null>(null);
  public settings$ = this.settingsSubject.asObservable();

  private defaultSettings: CompanySettings = {
    id: 1,
    company_name: 'Bilali Islamic Shopping',
    company_slogan: 'Thank you for your purchase!',
    date_format: 'dd/MM/yyyy HH:mm',
    thanks_message: 'Thank you for shopping with us!\nVisit again',
    currency_symbol: '₹',
    company_logo: '',
    payment_qr: '',
    invoice_header_layout: 'classic',
    label_template: 'classic',
    stock_maintenance_enabled: 1,
    default_bill_quantity: 1
  };

  constructor(private api: ApiService) {
    console.log('[SettingsService] Initializing...');
    // Set default settings immediately
    this.settingsSubject.next(this.defaultSettings);
    console.log('[SettingsService] Default settings set:', this.settings$);
    // Then load from API
    this.loadSettings();
  }

  /**
   * Normalize API response to CompanySettings (handles 0 and string numbers).
   */
  private normalizeSettings(response: any): CompanySettings {
    if (!response?.data) return this.defaultSettings as CompanySettings;
    const d = response.data;
    return {
      ...d,
      invoice_header_layout: String(d.invoice_header_layout || 'classic').trim() === 'logo_left_center'
        ? 'logo_left_center'
        : 'classic',
      label_template: String(d.label_template || 'classic').trim() === 'retail' ? 'retail' : 'classic',
      stock_maintenance_enabled: d.stock_maintenance_enabled !== undefined && d.stock_maintenance_enabled !== null
        ? (typeof d.stock_maintenance_enabled === 'string' ? parseInt(d.stock_maintenance_enabled, 10) : d.stock_maintenance_enabled)
        : 1,
      default_bill_quantity: Math.max(1, (d.default_bill_quantity !== undefined && d.default_bill_quantity !== null)
        ? (typeof d.default_bill_quantity === 'string' ? parseInt(d.default_bill_quantity, 10) : Number(d.default_bill_quantity))
        : 1)
    };
  }

  /**
   * Fetch settings from API and update in-memory state. Returns Observable so caller can subscribe and trigger the request.
   * Use this from the Settings page so the GET runs when the page loads (visible in Network tab).
   */
  fetchSettings(): Observable<CompanySettings> {
    console.log('[SettingsService] Fetching settings from API...');
    return this.api.get<any>('settings/settings_get.php').pipe(
      map((response) => {
        if (response?.success && response?.data) {
          const settings = this.normalizeSettings(response);
          this.settingsSubject.next(settings);
          console.log('[SettingsService] Settings fetched: hasim response?.data', response?.data);
          return settings;
        }
        this.settingsSubject.next(this.defaultSettings as CompanySettings);
        return this.defaultSettings as CompanySettings;
      }),
      catchError((err) => {
        console.error('[SettingsService] Failed to load settings:', err);
        this.settingsSubject.next(this.defaultSettings as CompanySettings);
        return of(this.defaultSettings as CompanySettings);
      })
    );
  }

  /**
   * Load settings from API (called from service constructor and when refreshing).
   */
  loadSettings(): void {
    this.fetchSettings().subscribe({
      next: () => {},
      error: () => {}
    });
  }

  /**
   * Get current settings (synchronous)
   */
  getSettings(): CompanySettings {
    return this.settingsSubject.value || this.defaultSettings;
  }

  /**
   * Get settings as observable
   */
  getSettings$(): Observable<CompanySettings | null> {
    return this.settings$;
  }

  /**
   * Update settings
   */
  updateSettings(settings: Partial<CompanySettings>): Observable<any> {
    return this.api.put('settings/settings_update.php', settings).pipe(
      tap((response: any) => {
        if (response.success && response.data) {
          // Convert stock_maintenance_enabled from string to number if needed
          const updatedSettings = {
            ...response.data,
            invoice_header_layout: String(response.data.invoice_header_layout || 'classic').trim() === 'logo_left_center'
              ? 'logo_left_center'
              : 'classic',
            label_template: String(response.data.label_template || 'classic').trim() === 'retail' ? 'retail' : 'classic',
            stock_maintenance_enabled: response.data.stock_maintenance_enabled !== undefined
              ? (typeof response.data.stock_maintenance_enabled === 'string'
                  ? parseInt(response.data.stock_maintenance_enabled, 10)
                  : response.data.stock_maintenance_enabled)
              : 1,
            default_bill_quantity: Math.max(1, (response.data.default_bill_quantity !== undefined && response.data.default_bill_quantity !== null)
              ? (typeof response.data.default_bill_quantity === 'string'
                  ? parseInt(response.data.default_bill_quantity, 10)
                  : Number(response.data.default_bill_quantity))
              : 1)
          };
          this.settingsSubject.next(updatedSettings);
        }
      })
    );
  }

  /**
   * Get company name with fallback to "Bilali Islamic Shopping"
   */
  getCompanyName(): string {
    const companyName = this.getSettings().company_name;
    return companyName && companyName.trim() ? companyName.trim() : 'Bilali Islamic Shopping';
  }

  /**
   * Get company slogan
   */
  getCompanySlogan(): string {
    return this.getSettings().company_slogan;
  }

  /**
   * Get date format
   */
  getDateFormat(): string {
    return this.getSettings().date_format;
  }

  /**
   * Get thanks message
   */
  getThanksMessage(): string {
    return this.getSettings().thanks_message;
  }

  /**
   * Get currency symbol
   */
  getCurrencySymbol(): string {
    return this.getSettings().currency_symbol;
  }

  /**
   * Check if stock maintenance is enabled
   */
  isStockMaintenanceEnabled(): boolean {
    const settings = this.getSettings();
    if (settings.stock_maintenance_enabled === undefined) {
      return true; // Default to enabled
    }
    // Handle both string "1" and number 1
    const value: any = settings.stock_maintenance_enabled;
    return value === 1 || value === '1' || value === true;
  }

  /**
   * Default quantity when adding a product to a bill. Minimum 1.
   */
  getDefaultBillQuantity(): number {
    const settings = this.getSettings();
    const raw = settings?.default_bill_quantity;
    if (raw === undefined || raw === null) return 1;
    const n = Number(raw);
    if (isNaN(n) || n < 1) return 1;
    return Math.floor(n);
  }
}

